/**
 * The page side of the Progsity IDE offline shell worker (`PG-PWA01`, ADR-0052).
 *
 * Registration happens only from the Playground shell routes, with a scope
 * of `/{locale}/playground`, so no other page on the site is ever controlled
 * by a worker. The worker itself is `public/playground-sw.js`; its rules are
 * tested from that file, and this module only knows where it lives and how
 * to remove it.
 *
 * Two of the three kill layers live here: `NEXT_PUBLIC_PLAYGROUND_SW=off`
 * turns registration into removal on the next visit, and `removeAll`
 * unregisters every Playground registration and deletes the caches. The
 * third is `public/playground-sw.kill.js` (`docs/08`).
 */

export const PLAYGROUND_SW_URL = "/playground-sw.js";
export const PLAYGROUND_SW_CACHE_PREFIX = "progsity-playground-";
export const SHELL_SOURCE_META_NAME = "progsity-shell-source";

export type PlaygroundSwRegistrationOutcome =
  | "registered"
  | "removed"
  | "unsupported"
  | "failed";

export function playgroundSwScope(locale: string): string {
  return `/${locale}/playground`;
}

/**
 * Production registers unless the env says `off`; development registers only
 * when it says `on`, because `next dev` serves unhashed chunks that a
 * cache-first rule would pin to a stale build.
 */
export function isPlaygroundServiceWorkerEnabled(
  env: { NODE_ENV?: string | undefined; NEXT_PUBLIC_PLAYGROUND_SW?: string | undefined } = {
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_PLAYGROUND_SW: process.env["NEXT_PUBLIC_PLAYGROUND_SW"],
  },
): boolean {
  const flag = (env.NEXT_PUBLIC_PLAYGROUND_SW ?? "").trim().toLowerCase();
  if (flag === "off") return false;
  if (env.NODE_ENV === "production") return true;
  return flag === "on";
}

export function serviceWorkerSupported(): boolean {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

function isPlaygroundRegistration(registration: ServiceWorkerRegistration): boolean {
  const worker = registration.active ?? registration.waiting ?? registration.installing;
  if (!worker) return false;
  try {
    return new URL(worker.scriptURL).pathname === PLAYGROUND_SW_URL;
  } catch {
    return false;
  }
}

/** Unregisters every Playground worker (both locale scopes) and drops its caches. */
export async function removePlaygroundServiceWorker(): Promise<void> {
  if (!serviceWorkerSupported()) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.filter(isPlaygroundRegistration).map((registration) =>
        registration.unregister().catch(() => false),
      ),
    );
  } catch {
    /* getRegistrations can throw in a restricted context; nothing to remove then */
  }
  if (typeof caches === "undefined") return;
  try {
    const names = await caches.keys();
    await Promise.all(
      names
        .filter((name) => name.startsWith(PLAYGROUND_SW_CACHE_PREFIX))
        .map((name) => caches.delete(name)),
    );
  } catch {
    /* storage unavailable */
  }
}

/**
 * The same-origin resources this page has loaded, as the warm-up list the
 * worker stores after its first activation (the worker applies its own
 * routing rules to each; this only keeps the list short and same-origin).
 */
export function warmupUrls(
  entryNames: readonly string[],
  origin: string = typeof location === "undefined" ? "" : location.origin,
): string[] {
  const out = new Set<string>();
  for (const name of entryNames) {
    let url: URL;
    try {
      url = new URL(name);
    } catch {
      continue;
    }
    if (url.origin !== origin) continue;
    const p = url.pathname;
    if (
      p.startsWith("/_next/static/") ||
      p.startsWith("/monaco/") ||
      p.startsWith("/pwa/") ||
      p === "/favicon.ico" ||
      p === "/icon_logo/logo-white.png" ||
      p === "/_next/image"
    ) {
      out.add(url.href);
    }
  }
  return [...out];
}

function loadedResourceNames(): string[] {
  if (typeof performance === "undefined" || typeof performance.getEntriesByType !== "function") {
    return [];
  }
  return performance.getEntriesByType("resource").map((entry) => entry.name);
}

const CONTROLLER_WAIT_MS = 8_000;

async function waitForController(): Promise<ServiceWorker | null> {
  if (navigator.serviceWorker.controller) return navigator.serviceWorker.controller;
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      navigator.serviceWorker.removeEventListener("controllerchange", onChange);
      resolve(navigator.serviceWorker.controller);
    }, CONTROLLER_WAIT_MS);
    const onChange = () => {
      window.clearTimeout(timer);
      resolve(navigator.serviceWorker.controller);
    };
    navigator.serviceWorker.addEventListener("controllerchange", onChange, { once: true });
  });
}

/**
 * Registers the worker for one locale, or removes it when the switch is off.
 * `updateViaCache: "none"` means a replaced script (the kill file included)
 * is fetched fresh on every update check rather than from the HTTP cache.
 *
 * After registration the page hands the worker what it has already loaded
 * (the shell pathname and its same-origin resources), because a first visit
 * fetched all of that before any worker existed; without the warm-up, the
 * shell would only reopen offline from the second online visit.
 */
export async function registerPlaygroundServiceWorker(
  locale: string,
): Promise<PlaygroundSwRegistrationOutcome> {
  if (!serviceWorkerSupported()) return "unsupported";
  if (!isPlaygroundServiceWorkerEnabled()) {
    await removePlaygroundServiceWorker();
    return "removed";
  }
  try {
    await navigator.serviceWorker.register(PLAYGROUND_SW_URL, {
      scope: playgroundSwScope(locale),
      updateViaCache: "none",
    });
  } catch {
    return "failed";
  }
  try {
    await navigator.serviceWorker.ready;
    const controller = await waitForController();
    controller?.postMessage({
      type: "progsity-playground-sw:warm",
      pathname: window.location.pathname,
      urls: warmupUrls(loadedResourceNames()),
    });
  } catch {
    /* the warm-up is best effort; the next navigation stores the shell anyway */
  }
  return "registered";
}

/** Whether this document was served by the worker from its cache. */
export function readShellSource(doc: Document | undefined = typeof document === "undefined" ? undefined : document): "cache" | "network" {
  if (!doc) return "network";
  const meta = doc.querySelector(`meta[name="${SHELL_SOURCE_META_NAME}"]`);
  return meta?.getAttribute("content") === "cache" ? "cache" : "network";
}
